import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Range } from "react-range";
import { useFetchVideos } from "../../hooks/queries/useFetchVideos";
import { useUploadVideo } from "../../hooks/mutations/useUploadVideo";
import { useDeleteVideo } from "../../hooks/mutations/useDeleteVideo";
import { useUpdateDetection } from "../../hooks/mutations/useUpdateDetection";
import { getVideoSignedUrl, exportVideo, getAnnotatedVideoUrl } from "../../api/videos";
import { fetchRouteByVideoId } from "../../api/routes";
import { Spinner } from "../../components/ui/Spinner";
import ConfirmDialog from "../ui/ConfirmDialog";
import { Pagination } from "../ui/Pagination";
import DetectionRouteMap from "./DetectionRouteMap";
import { getMalfunctionTypeLabel } from "../../utils/malfunctions";
import SurfaceRatingBadge from "../ui/SurfaceRatingBadge";

function DeleteTileIcon() {
  const maskId = useId();
  return (
    <svg width="39" height="40" viewBox="0 0 39 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="7" y="8" width="25" height="24">
        <rect x="7.5" y="8" width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M14.8077 28.5017C14.3106 28.5017 13.885 28.3247 13.531 27.9707C13.177 27.6167 13 27.1911 13 26.6939V14.0017H12.75C12.5375 14.0017 12.3594 13.9298 12.2158 13.7859C12.0719 13.6421 12 13.4639 12 13.2514C12 13.0388 12.0719 12.8607 12.2158 12.7172C12.3594 12.5735 12.5375 12.5017 12.75 12.5017H16.5C16.5 12.2569 16.5863 12.0482 16.7588 11.8757C16.9311 11.7034 17.1397 11.6172 17.3845 11.6172H21.6155C21.8603 11.6172 22.0689 11.7034 22.2413 11.8757C22.4138 12.0482 22.5 12.2569 22.5 12.5017H26.25C26.4625 12.5017 26.6406 12.5736 26.7843 12.7174C26.9281 12.8613 27 13.0394 27 13.2519C27 13.4646 26.9281 13.6427 26.7843 13.7862C26.6406 13.9299 26.4625 14.0017 26.25 14.0017H26V26.6939C26 27.1911 25.823 27.6167 25.469 27.9707C25.115 28.3247 24.6894 28.5017 24.1923 28.5017H14.8077ZM17.6543 25.0017C17.8668 25.0017 18.0448 24.9299 18.1885 24.7862C18.332 24.6424 18.4037 24.4642 18.4037 24.2517V16.7517C18.4037 16.5392 18.3318 16.361 18.188 16.2172C18.0443 16.0735 17.8662 16.0017 17.6535 16.0017C17.441 16.0017 17.2629 16.0735 17.1192 16.2172C16.9757 16.361 16.904 16.5392 16.904 16.7517V24.2517C16.904 24.4642 16.9758 24.6424 17.1195 24.7862C17.2633 24.9299 17.4416 25.0017 17.6543 25.0017ZM21.3465 25.0017C21.559 25.0017 21.7371 24.9299 21.8807 24.7862C22.0243 24.6424 22.096 24.4642 22.096 24.2517V16.7517C22.096 16.5392 22.0242 16.361 21.8805 16.2172C21.7367 16.0735 21.5584 16.0017 21.3458 16.0017C21.1333 16.0017 20.9552 16.0735 20.8115 16.2172C20.668 16.361 20.5962 16.5392 20.5962 16.7517V24.2517C20.5962 24.4642 20.6682 24.6424 20.812 24.7862C20.9557 24.9299 21.1338 25.0017 21.3465 25.0017Z" fill="currentColor" />
      </g>
    </svg>
  );
}

function ViewTileIcon() {
  const maskId = useId();
  return (
    <svg width="39" height="40" viewBox="0 0 39 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="8" y="8" width="24" height="24">
        <rect x="8" y="8" width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M20.1982 23.577C21.3314 23.577 22.2938 23.1804 23.0855 22.3872C23.8772 21.5941 24.273 20.6309 24.273 19.4978C24.273 18.3646 23.8764 17.4022 23.0832 16.6105C22.2901 15.8188 21.3269 15.423 20.1938 15.423C19.0606 15.423 18.0982 15.8196 17.3065 16.6128C16.5148 17.4059 16.119 18.3691 16.119 19.5023C16.119 20.6354 16.5156 21.5978 17.3088 22.3895C18.1019 23.1812 19.0651 23.577 20.1982 23.577ZM20.196 22.2C19.446 22.2 18.8085 21.9375 18.2835 21.4125C17.7585 20.8875 17.496 20.25 17.496 19.5C17.496 18.75 17.7585 18.1125 18.2835 17.5875C18.8085 17.0625 19.446 16.8 20.196 16.8C20.946 16.8 21.5835 17.0625 22.1085 17.5875C22.6335 18.1125 22.896 18.75 22.896 19.5C22.896 20.25 22.6335 20.8875 22.1085 21.4125C21.5835 21.9375 20.946 22.2 20.196 22.2ZM20.196 26.5C18.0908 26.5 16.1655 25.9384 14.42 24.8152C12.6745 23.6922 11.2812 22.2148 10.2402 20.3828C10.1569 20.2391 10.096 20.0943 10.0575 19.9485C10.0192 19.8027 10 19.653 10 19.4995C10 19.346 10.0192 19.1965 10.0575 19.051C10.096 18.9055 10.1569 18.7609 10.2402 18.6172C11.2812 16.7852 12.6745 15.3078 14.42 14.1848C16.1655 13.0616 18.0908 12.5 20.196 12.5C22.3012 12.5 24.2265 13.0616 25.972 14.1848C27.7175 15.3078 29.1107 16.7852 30.1517 18.6172C30.2351 18.7609 30.296 18.9057 30.3345 19.0515C30.3728 19.1973 30.392 19.347 30.392 19.5005C30.392 19.654 30.3728 19.8035 30.3345 19.949C30.296 20.0945 30.2351 20.2391 30.1517 20.3828C29.1107 22.2148 27.7175 23.6922 25.972 24.8152C24.2265 25.9384 22.3012 26.5 20.196 26.5Z" fill="currentColor" />
      </g>
    </svg>
  );
}

function DetectionMapIcon({ fill = "#BFBFBF" }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2.75C8.95 2.75 6.5 5.22 6.5 8.25C6.5 12.37 12 19.25 12 19.25C12 19.25 17.5 12.37 17.5 8.25C17.5 5.22 15.05 2.75 12 2.75ZM12 10.25C10.9 10.25 10 9.35 10 8.25C10 7.15 10.9 6.25 12 6.25C13.1 6.25 14 7.15 14 8.25C14 9.35 13.1 10.25 12 10.25Z"
        fill={fill}
      />
      <path
        d="M5.1 15.85L3.35 20.25H20.65L18.9 15.85H17.28C16.94 16.38 16.59 16.9 16.25 17.39L17.84 17.39L18.33 18.75H5.67L6.16 17.39H7.75C7.41 16.9 7.06 16.38 6.72 15.85H5.1Z"
        fill={fill}
      />
    </svg>
  );
}

function SortChevron({ active, direction }) {
  return (
    <span className={`ml-[4px] inline-flex h-[14px] w-[14px] items-center justify-center ${active ? "text-[#483ED1]" : "text-[#BFBFBF]"}`}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
        className={direction === "asc" ? "rotate-180" : ""}
      >
        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function DownloadSplitTileIcon() {
  const maskId0 = useId();
  const maskId1 = useId();
  return (
    <svg width="66" height="40" viewBox="0 0 66 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="36.5" y1="7.5" x2="36.5" y2="32.5" stroke="currentColor" strokeLinecap="round" />
      <mask id={maskId0} style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="8" y="8" width="24" height="24">
        <rect x="8.5" y="8.5" width="23" height="23" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId0})`}>
        <path d="M26.75 22.9932C26.9596 22.9932 27.1344 23.064 27.2754 23.2051C27.4165 23.346 27.4872 23.5209 27.4873 23.7305V25.6924C27.4873 26.1943 27.3134 26.6182 26.9658 26.9658C26.6182 27.3134 26.1943 27.4873 25.6924 27.4873H14.3076C13.8057 27.4873 13.3818 27.3134 13.0342 26.9658C12.6866 26.6182 12.5127 26.1943 12.5127 25.6924V23.7305C12.5128 23.5209 12.5835 23.346 12.7246 23.2051C12.8656 23.064 13.0404 22.9932 13.25 22.9932C13.4596 22.9932 13.6344 23.064 13.7754 23.2051C13.9165 23.346 13.9872 23.5209 13.9873 23.7305V25.6924C13.9873 25.773 14.0212 25.8464 14.0869 25.9121C14.1528 25.9781 14.2268 26.0127 14.3076 26.0127H25.6924C25.7732 26.0127 25.8472 25.9781 25.9131 25.9121L25.9121 25.9111C25.9778 25.8454 26.0127 25.773 26.0127 25.6924V23.7305C26.0128 23.5209 26.0835 23.346 26.2246 23.2051C26.3656 23.064 26.5404 22.9932 26.75 22.9932ZM20 12.5127C20.2097 12.5127 20.3844 12.5834 20.5254 12.7246C20.6666 12.8656 20.7373 13.0403 20.7373 13.25V20.9307L20.7588 20.9092L22.6807 18.9854L22.6816 18.9863C22.8281 18.8401 23.0015 18.7678 23.2021 18.7695C23.3529 18.771 23.4906 18.8142 23.6143 18.9004L23.7334 19.001C23.8762 19.154 23.95 19.3268 23.9551 19.5195C23.9601 19.7118 23.8864 19.8841 23.7334 20.0371L20.624 23.1465C20.5314 23.2391 20.4339 23.3042 20.332 23.3418C20.2297 23.3797 20.1191 23.3994 20 23.3994C19.8809 23.3994 19.7703 23.3797 19.668 23.3418C19.5661 23.3042 19.4686 23.2391 19.376 23.1465L16.2666 20.0371C16.1203 19.8906 16.0481 19.72 16.0498 19.5244C16.0513 19.3773 16.0921 19.242 16.1729 19.1191L16.2666 19.001C16.4197 18.8481 16.5953 18.7697 16.793 18.7646C16.9903 18.7597 17.1655 18.8326 17.3184 18.9854L19.2412 20.9092L19.2627 20.9307V13.25C19.2627 13.0403 19.3334 12.8656 19.4746 12.7246C19.6156 12.5834 19.7903 12.5127 20 12.5127Z" fill="currentColor" stroke="currentColor" strokeWidth="0.025" />
      </g>
      <mask id={maskId1} style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="39" y="8" width="24" height="24">
        <rect x="39.5" y="8.5" width="23" height="23" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId1})`}>
        <path d="M47.6812 18.2627H54.3188C54.4512 18.2627 54.557 18.3071 54.6372 18.3955C54.7179 18.4844 54.7583 18.5876 54.7583 18.7061C54.7583 18.7114 54.7563 18.7223 54.7505 18.7393C54.7448 18.7558 54.7357 18.7775 54.7241 18.8037C54.701 18.8559 54.6658 18.9263 54.6196 19.0156L51.4712 22.165C51.3998 22.2366 51.326 22.2887 51.2505 22.3213C51.175 22.3538 51.0916 22.3701 51.0005 22.3701C50.9092 22.3701 50.8251 22.3539 50.7495 22.3213C50.674 22.2887 50.6002 22.2366 50.5288 22.165L47.3774 19.0146C47.3561 18.9933 47.3375 18.9702 47.3208 18.9463L47.2778 18.8721C47.2536 18.8203 47.2417 18.7646 47.2417 18.7051C47.2417 18.5857 47.2821 18.4818 47.3628 18.3936C47.443 18.3063 47.5489 18.2627 47.6812 18.2627Z" fill="currentColor" stroke="currentColor" strokeWidth="0.025" />
      </g>
    </svg>
  );
}

function EmptyVideosIcon() {
  return (
    <svg width="82" height="82" viewBox="0 0 82 82" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-80">
      <g opacity="0.8">
        <path fillRule="evenodd" clipRule="evenodd" d="M54.1351 40.6117L40.5449 54.2006C40.5449 54.2006 45.3778 52.6913 52.3235 55.1065C53.492 55.5126 55.4471 53.5574 55.041 52.3889C52.6258 45.4433 54.1351 40.6117 54.1351 40.6117Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M17.7636 17.8298C26.0149 9.57984 39.4104 9.57984 47.6616 17.8298C55.9116 26.0811 55.9116 39.4765 47.6616 47.7278C39.4104 55.9777 26.0149 55.9777 17.7636 47.7278C9.51368 39.4765 9.51368 26.0811 17.7636 17.8298Z" fill="#E9F3FC"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M44.9279 15.521C45.8863 16.2013 46.8011 16.9714 47.6608 17.8298C55.9108 26.0811 55.9108 39.4765 47.6608 47.7278C39.4096 55.9777 26.0141 55.9777 17.7628 47.7278C17.2773 47.2409 16.8198 46.7374 16.3906 46.2185C24.3882 48.8168 33.5286 46.9411 39.8785 40.5912C46.6512 33.8185 48.3348 23.8709 44.9279 15.521Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M15.9516 16.0182C25.2023 6.76754 40.2224 6.76754 49.473 16.0182C58.7236 25.2688 58.7236 40.2889 49.473 49.5395C40.2224 58.7901 25.2023 58.7901 15.9516 49.5395C6.70102 40.2889 6.70102 25.2688 15.9516 16.0182ZM18.6692 18.7357C26.4195 10.9854 39.0052 10.9854 46.7555 18.7357C54.5057 26.486 54.5057 39.0717 46.7555 46.822C39.0052 54.5723 26.4195 54.5723 18.6692 46.822C10.9189 39.0717 10.9189 26.486 18.6692 18.7357Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M11.4763 43.319C7.07904 34.454 8.57042 23.3994 15.9517 16.0181C22.8833 9.08655 33.0538 7.34918 41.6113 10.806C41.6728 11.5914 41.7048 12.3871 41.7048 13.1904C41.7048 13.8105 41.6856 14.4268 41.6485 15.0392C34.2031 11.2903 24.8833 12.5229 18.6692 18.7356C12.0823 25.3225 11.0932 35.4021 15.7044 43.0384C14.354 43.2229 12.9754 43.319 11.5762 43.319H11.4763Z" fill="#B9CAE3"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L78.0109 69.9242C78.5106 70.4239 77.0897 72.6571 74.8398 74.907C72.59 77.1569 70.3568 78.5778 69.8571 78.0781L51.2841 59.5051Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L76.2479 68.1612C76.7476 68.6609 75.3267 70.8941 73.0768 73.144C70.827 75.3939 68.5938 76.8148 68.0941 76.3151L51.2841 59.5051Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M29.9982 28.7448C29.9687 27.5238 30.4274 26.632 31.1347 26.1093C32.2468 25.2893 33.9086 25.3239 34.8106 26.3399C35.1975 26.7768 35.4217 27.3944 35.4281 28.1759C35.4435 30.0504 34.5646 31.1459 33.7036 32.2798C32.5453 33.8083 31.3883 35.3791 31.2448 38.0018C31.2064 38.7091 31.7497 39.3125 32.4556 39.351C33.1616 39.3894 33.7664 38.8474 33.8048 38.1415C33.9124 36.1491 34.8657 34.9883 35.7459 33.8275C36.9246 32.2734 38.0124 30.723 37.9906 28.1554C37.9778 26.6461 37.4743 25.4814 36.726 24.6384C34.9784 22.6704 31.7689 22.4577 29.6138 24.0464C28.3223 24.9997 27.3832 26.5782 27.4357 28.805C27.4523 29.5123 28.0404 30.0722 28.7477 30.0555C29.4537 30.0389 30.0148 29.452 29.9982 28.7448Z" fill="#8DA5CF"/>
        <path d="M32.406 44.1095C33.2764 44.1095 33.982 43.4039 33.982 42.5336C33.982 41.6632 33.2764 40.9576 32.406 40.9576C31.5356 40.9576 30.8301 41.6632 30.8301 42.5336C30.8301 43.4039 31.5356 44.1095 32.406 44.1095Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M55.4638 10.8867L59.3076 4.48048C59.6715 3.87445 59.4754 3.08648 58.8681 2.72261C58.2621 2.36001 57.4741 2.55604 57.1103 3.16207L53.2665 9.56833C52.9026 10.1756 53.0999 10.9636 53.706 11.3262C54.3133 11.6901 55.1 11.494 55.4638 10.8867Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M61.5248 15.1084L75.6185 4.85845C76.1899 4.44204 76.3168 3.63998 75.9017 3.06854C75.4853 2.49582 74.6832 2.37026 74.1118 2.78539L60.018 13.0354C59.4453 13.4518 59.3197 14.2539 59.7349 14.8253C60.1513 15.398 60.9533 15.5236 61.5248 15.1084Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M63.6442 23.0022L73.8942 20.4397C74.5809 20.268 74.9986 19.5723 74.8269 18.8856C74.6552 18.2001 73.9595 17.7824 73.2728 17.9541L63.0228 20.5166C62.3373 20.6883 61.9196 21.384 62.0913 22.0708C62.263 22.7562 62.9587 23.1739 63.6442 23.0022Z" fill="#8DA5CF"/>
      </g>
    </svg>
  );
}

const calendarWeekdayLabels = ["M", "T", "W", "T", "F", "S", "S"];

const toIsoDate = (date) => date.toISOString().split("T")[0];
const parseIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isWithinRange = (day, start, end) => {
  if (!day || !start || !end) return false;
  return day > start && day < end;
};

const buildCalendarMatrix = (year, month) => {
  // Monday-based grid
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  // Start from Monday of the week containing the 1st
  const start = new Date(firstOfMonth);
  const startOffset = (start.getDay() + 6) % 7; // Mon=0 ... Sun=6
  start.setDate(start.getDate() - startOffset);

  // End at Sunday of the week containing the last day of month
  const end = new Date(lastOfMonth);
  const endOffset = 6 - ((end.getDay() + 6) % 7);
  end.setDate(end.getDate() + endOffset);

  const weeks = [];
  let cursor = new Date(start);

  while (cursor <= end) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      const date = new Date(cursor);
      const inMonth = date.getMonth() === month;

      week.push({
        date,
        label: String(date.getDate()), // keep numeric; you pad in render
        inMonth,
      });

      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};

export default function VideoList({ uploadOpen = false, onCloseUpload }) {
  const PROBLEMS_MIN = 0;
  const PROBLEMS_MAX = 100;

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter state (must be declared before `useFetchVideos`)
  const [statusFilter, setStatusFilter] = useState("");
  const [statusOpen, setStatusOpen] = useState(false);
  const [processedFilter, setProcessedFilter] = useState("");
  const [processedOpen, setProcessedOpen] = useState(false);
  const [problemsOpen, setProblemsOpen] = useState(false);
  const [problemsRange, setProblemsRange] = useState([PROBLEMS_MIN, PROBLEMS_MAX]);
  const [minMalfunctions, setMinMalfunctions] = useState("");
  const [maxMalfunctions, setMaxMalfunctions] = useState("");
  const [unsolvedOnly, setUnsolvedOnly] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [ordering, setOrdering] = useState("-timestamp");

  const { data, isLoading, isError, error } = useFetchVideos({
    page,
    pageSize,
    status: statusFilter,
    processed: processedFilter,
    minMalfunctions,
    maxMalfunctions,
    startDate,
    endDate,
    unsolvedOnly,
    ordering,
  });
  const videos = useMemo(() => {
    return Array.isArray(data) ? data : (data?.results ?? []);
  }, [data]);
  
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const queryClient = useQueryClient();
  const uploadMutation = useUploadVideo();
  const deleteMutation = useDeleteVideo();
  const updateDetectionMut = useUpdateDetection();

  const [file, setFile] = useState(null);
  const [routeFile, setRouteFile] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null); // 0-100, null when idle
  const [selected, setSelected] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [exporting, setExporting] = useState({}); 
  const videoRef = useRef(null);
  const [previewMode, setPreviewMode] = useState("video");
  const [selectedDetectionForMap, setSelectedDetectionForMap] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeLookupVideoId, setRouteLookupVideoId] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState("");
  const [pendingVideoSeek, setPendingVideoSeek] = useState(null);

  const [showAnnotated, setShowAnnotated] = useState(false);
  const [annotatedUrl, setAnnotatedUrl] = useState("");

  const [exportMenuFor, setExportMenuFor] = useState(null);
  const [exportMenuPosition, setExportMenuPosition] = useState({ top: 0, left: 0 });
  
  const videoInputRef = useRef(null);
  const routeInputRef = useRef(null);

  const resetUploadForm = () => {
    setFile(null);
    setRouteFile(null);
    setValidationError("");
    setUploadProgress(null);
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (routeInputRef.current) routeInputRef.current.value = "";
  };

  const closeUploadModal = () => {
    resetUploadForm();
    onCloseUpload?.();
  };

  const resetAnnotatedState = () => {
    setShowAnnotated(false);
    setAnnotatedUrl("");
  };

  const resetMapPreviewState = () => {
    setPreviewMode("video");
    setSelectedDetectionForMap(null);
    setSelectedRoute(null);
    setRouteLookupVideoId(null);
    setRouteLoading(false);
    setRouteError("");
    setPendingVideoSeek(null);
  };

  useEffect(() => {
    if (!uploadOpen) resetUploadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadOpen]);

  useEffect(() => {
    const onWindowClick = () => setExportMenuFor(null);
    window.addEventListener("click", onWindowClick);
    return () => window.removeEventListener("click", onWindowClick);
  }, []);

  useEffect(() => {
    if (!exportMenuFor) return;

    const onScrollOrResize = () => setExportMenuFor(null);
    const onKeyDown = (e) => {
      if (e.key === "Escape") setExportMenuFor(null);
    };

    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [exportMenuFor]);

  useEffect(() => {
    if (!selected?.id || !showAnnotated) return undefined;

    let cancelled = false;
    const key = `${selected.id}-annotated`;

    setExporting((prev) => ({ ...prev, [key]: true }));

    (async () => {
      try {
        const url = await getAnnotatedVideoUrl(selected.id);
        if (!cancelled) setAnnotatedUrl(url);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setShowAnnotated(false);
          setAnnotatedUrl("");
          alert("Failed to load annotated video");
        }
      } finally {
        if (!cancelled) setExporting((prev) => ({ ...prev, [key]: false }));
      }
    })();

    return () => {
      cancelled = true;
      setExporting((prev) => ({ ...prev, [key]: false }));
    };
  }, [selected?.id, showAnnotated]);

  useEffect(() => {
    if (previewMode !== "map" || !selected?.id || routeLookupVideoId === selected.id) {
      return undefined;
    }

    let cancelled = false;

    setRouteLoading(true);
    setRouteError("");
    setSelectedRoute(null);

    (async () => {
      try {
        const route = await fetchRouteByVideoId(selected.id);
        if (!cancelled) setSelectedRoute(route);
      } catch (err) {
        console.error("Error loading route for video:", err);
        if (!cancelled) setRouteError("Route map could not be loaded");
      } finally {
        if (!cancelled) {
          setRouteLoading(false);
          setRouteLookupVideoId(selected.id);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [previewMode, selected?.id, routeLookupVideoId]);

  useEffect(() => {
    if (!pendingVideoSeek || !videoRef.current) return;

    videoRef.current.currentTime = pendingVideoSeek.timecode;

    if (pendingVideoSeek.play) {
      const playPromise = videoRef.current.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      videoRef.current.pause();
    }

    setPendingVideoSeek(null);
  }, [pendingVideoSeek, previewMode, showAnnotated]);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, videoId: null });

  const today = new Date();
  const [calendarOpenFor, setCalendarOpenFor] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(today.getMonth());
  const [calendarYear, setCalendarYear] = useState(today.getFullYear());
  const calendarDropdownRef = useRef(null);
  const startCalendarTriggerRef = useRef(null);
  const endCalendarTriggerRef = useRef(null);
  const filtersBarRef = useRef(null);

  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const statusDropdownRef = useRef(null);
  const processedDropdownRef = useRef(null);
  const problemsDropdownRef = useRef(null);

  const [activeAction, setActiveAction] = useState(null);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const statusOutside = statusDropdownRef.current && !statusDropdownRef.current.contains(e.target);
      const processedOutside = processedDropdownRef.current && !processedDropdownRef.current.contains(e.target);
      const problemsOutside = problemsDropdownRef.current && !problemsDropdownRef.current.contains(e.target);
      if (statusOutside) setStatusOpen(false);
      if (processedOutside) setProcessedOpen(false);
      if (problemsOutside) setProblemsOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const syncProblemsRangeFromFilters = () => {
    const currentMin = minMalfunctions !== "" ? Number(minMalfunctions) : PROBLEMS_MIN;
    const currentMax = maxMalfunctions !== "" ? Number(maxMalfunctions) : PROBLEMS_MAX;
    const clampedMin = Number.isFinite(currentMin) ? Math.min(Math.max(currentMin, PROBLEMS_MIN), PROBLEMS_MAX) : PROBLEMS_MIN;
    const clampedMax = Number.isFinite(currentMax) ? Math.min(Math.max(currentMax, PROBLEMS_MIN), PROBLEMS_MAX) : PROBLEMS_MAX;
    const next = clampedMin <= clampedMax ? [clampedMin, clampedMax] : [clampedMax, clampedMin];
    setProblemsRange(next);
  };

  const clearFilters = () => {
    setStatusFilter("");
    setProcessedFilter("");
    setMinMalfunctions("");
    setMaxMalfunctions("");
    setProblemsRange([PROBLEMS_MIN, PROBLEMS_MAX]);
    setStatusOpen(false);
    setProcessedOpen(false);
    setProblemsOpen(false);
    setUnsolvedOnly(false);
    setStartDate("");
    setEndDate("");
  };

  const calendarMatrix = useMemo(
    () => buildCalendarMatrix(calendarYear, calendarMonth),
    [calendarMonth, calendarYear]
  );

  const parsedStartDate = parseIsoDate(startDate);
  const parsedEndDate = parseIsoDate(endDate);
  const activeMonthLabel = new Date(calendarYear, calendarMonth).toLocaleString("en-US", { month: "long" });

  const formatCalendarInput = (value) => {
    if (!value) return "";
    const parsed = parseIsoDate(value);
    return parsed ? parsed.toLocaleDateString("en-GB") : "";
  };

  const handleCalendarNav = (direction) => {
    const next = new Date(calendarYear, calendarMonth + direction);
    setCalendarYear(next.getFullYear());
    setCalendarMonth(next.getMonth());
  };

  const openCalendar = (target) => {
    if (calendarOpenFor === target) {
      setCalendarOpenFor(null);
      return;
    }

    const triggerEl = target === "start" ? startCalendarTriggerRef.current : endCalendarTriggerRef.current;
    const containerEl = filtersBarRef.current;
    if (triggerEl && containerEl) {
      const triggerRect = triggerEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      const overlayWidth = 222;
      const padding = 16;
      const desiredLeft = triggerRect.left - containerRect.left;
      const maxLeft = Math.max(padding, containerRect.width - overlayWidth - padding);
      const clampedLeft = Math.max(padding, Math.min(desiredLeft, maxLeft));

      setCalendarPosition({
        top: triggerRect.bottom - containerRect.top + 8,
        left: clampedLeft,
      });
    }

    const referenceIso = target === "start" ? startDate : endDate;
    const fallbackIso = startDate || endDate;
    const referenceDate = parseIsoDate(referenceIso) || parseIsoDate(fallbackIso) || new Date();
    if (referenceDate) {
      setCalendarYear(referenceDate.getFullYear());
      setCalendarMonth(referenceDate.getMonth());
    }
    setCalendarOpenFor(target);
  };

  const handleDaySelect = (date) => {
    if (!calendarOpenFor) return;
    const iso = toIsoDate(date);
    if (calendarOpenFor === "start") {
      setStartDate(iso);
      if (endDate && parseIsoDate(endDate) && parseIsoDate(endDate) < date) {
        setEndDate(iso);
      }
    } else if (calendarOpenFor === "end") {
      setEndDate(iso);
      if (startDate && parseIsoDate(startDate) && parseIsoDate(startDate) > date) {
        setStartDate(iso);
      }
    }
    setCalendarOpenFor(null);
  };

  const clearCalendarSelection = () => {
    setStartDate("");
    setEndDate("");
    setCalendarOpenFor(null);
  };

  useEffect(() => {
    if (!calendarOpenFor) return undefined;
    const onDocClick = (event) => {
      if (
        calendarDropdownRef.current &&
        !calendarDropdownRef.current.contains(event.target) &&
        !startCalendarTriggerRef.current?.contains(event.target) &&
        !endCalendarTriggerRef.current?.contains(event.target)
      ) {
        setCalendarOpenFor(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [calendarOpenFor]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, processedFilter, minMalfunctions, maxMalfunctions, unsolvedOnly, startDate, endDate, ordering]);

  const getSortState = (field) => {
    if (ordering === field) return { active: true, direction: "asc" };
    if (ordering === `-${field}`) return { active: true, direction: "desc" };
    return { active: false, direction: "desc" };
  };

  const toggleSort = (field) => {
    setOrdering((current) => (current === `-${field}` ? field : `-${field}`));
  };

  const SortHeader = ({ field, children }) => {
    const sort = getSortState(field);
    return (
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className={`inline-flex items-center text-left transition-colors hover:text-[#483ED1] ${sort.active ? "text-[#483ED1]" : "text-[#828282]"}`}
        title={`Sort by ${children}`}
      >
        <span>{children}</span>
        <SortChevron active={sort.active} direction={sort.direction} />
      </button>
    );
  };

  const openPreview = async (v) => {
    resetAnnotatedState();
    resetMapPreviewState();
    setSelected(v);
    try {
      const fresh = await getVideoSignedUrl(v.id);
      setPreviewUrl(makeFullUrl(fresh));
    } catch (e) {
      setPreviewUrl(makeFullUrl(v.video));
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] ?? null);
    setValidationError("");
  };

  const handleRouteFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && !selectedFile.name.toLowerCase().endsWith('.gpx')) {
      setValidationError("Route file must be a .gpx file");
      setRouteFile(null);
      e.target.value = null;
      return;
    }
    setRouteFile(selectedFile ?? null);
    setValidationError("");
  };

  const handleUpload = (e) => {
    e.preventDefault();
    setUploadProgress(0);
    if (!file) {
      setValidationError("Video file is required");
      return;
    }
    if (!routeFile) {
      setValidationError("Route GPX file is required");
      return;
    }

    uploadMutation.mutate(
      {
        video: file,
        route_file: routeFile,
        onUploadProgress: (pct) => setUploadProgress(pct),
      },
      {
        onSuccess: () => {
          resetUploadForm();
          queryClient.invalidateQueries({ queryKey: ["videos"] });
          queryClient.invalidateQueries({ queryKey: ["routes"] });
          onCloseUpload?.();
        },
        onError: (err) => {
          const detail = err.response?.data?.detail;
          const videoErr = err.response?.data?.video?.[0];
          const routeErr = err.response?.data?.route_file?.[0];
          setValidationError(detail || videoErr || routeErr || "Upload failed");
          setUploadProgress(null);
        },
      }
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-GB");
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("el-GR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    setDeleteConfirm({ isOpen: true, videoId: id });
  };

  const confirmDelete = () => {
    const { videoId } = deleteConfirm;
    if (selected?.id === videoId) {
      setSelected(null);
      resetMapPreviewState();
    }
    deleteMutation.mutate(videoId);
    setDeleteConfirm({ isOpen: false, videoId: null });
  };

  const handleExport = async (e, videoId, format) => {
    e.stopPropagation();
    const key = `${videoId}-${format}`;
    setExporting((prev) => ({ ...prev, [key]: true }));
    try {
      await exportVideo(videoId, format);
    } catch (err) {
      console.error(`Error exporting ${format}:`, err);
      alert(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleCheckboxChange = (detectionId, field, currentValue) => {
    const newValue = !currentValue;
    updateDetectionMut.mutate(
      { detectionId, field, value: newValue },
      {
        onSuccess: (updatedDetection) => {
          setSelected((prev) => ({
            ...prev,
            detections: prev.detections.map((d) =>
              d.id === detectionId ? updatedDetection : d
            ),
          }));
        },
      }
    );
  };

  const seekVideoToDetection = (detection, { play = false } = {}) => {
    setPendingVideoSeek({ timecode: detection.timecode, play });
  };

  const jumpToDetection = (detection) => {
    setPreviewMode("video");
    seekVideoToDetection(detection, { play: true });
  };

  const hasDetectionCoords = (detection) => (
    detection?.latitude != null && detection?.longitude != null
  );

  const openDetectionMap = (detection) => {
    if (!hasDetectionCoords(detection)) return;
    setSelectedDetectionForMap(detection);
    setPreviewMode("map");
    seekVideoToDetection(detection);
  };

  const makeFullUrl = (path) => {
    if (!path) return "";
    const base = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");
    if (/^https?:\/\//.test(path)) {
      try {
        const url = new URL(path);
        const apiUrl = new URL(base);
        if (
          url.pathname.startsWith("/media/") &&
          ["localhost", "127.0.0.1"].includes(url.hostname) &&
          !url.port
        ) {
          url.protocol = apiUrl.protocol;
          url.hostname = apiUrl.hostname;
          url.port = apiUrl.port;
          return url.toString();
        }
      } catch (_) {
        return path;
      }
      return path;
    }
    const relative = path.startsWith("/") ? path : `/${path}`;
    return `${base}${relative}`;
  };

  if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Spinner className="h-12 w-12 text-camber-blue-500 mx-auto mb-4" />
            <p className="text-camber-gray-600">Loading videos...</p>
          </div>
        </div>
      );
    }
  if (isError) return <p className="text-red-600 font-medium">Error: {error.message}</p>;

  const hasFilters = !!(
    statusFilter ||
    processedFilter ||
    minMalfunctions ||
    maxMalfunctions ||
    unsolvedOnly ||
    startDate ||
    endDate
  );
  const emptyMessage = hasFilters ? "No videos match your filters." : "No videos found.";

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  const openExportMenuAtEvent = (videoId, e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    const menuWidth = 66;
    const menuHeight = 38 + 1 + 38;
    const margin = 8;

    let left = rect.right - menuWidth;
    left = Math.min(Math.max(left, margin), window.innerWidth - menuWidth - margin);

    let top = rect.bottom;
    const wouldOverflowBottom = top + menuHeight + margin > window.innerHeight;
    const canPlaceAbove = rect.top - menuHeight - margin >= 0;
    if (wouldOverflowBottom && canPlaceAbove) top = rect.top - menuHeight;
    top = Math.min(Math.max(top, margin), window.innerHeight - menuHeight - margin);

    setExportMenuPosition({ top, left });
    setExportMenuFor(videoId);
  };

  return (
    <div className="w-full font-['Open_Sans']" onClick={() => {
      setExportMenuFor(null);
      setActiveAction(null);
      setIsPreviewOpen(false);
      resetMapPreviewState();
    }}>
      {/* Upload Modal */}
      {uploadOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#131313]/[0.69]"
          onClick={() => {
            if (uploadMutation.isPending) return;
            closeUploadModal();
          }}
        >
          <div className="relative w-[435px] h-[348px] bg-white rounded-[8px] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {uploadMutation.isPending && (
              <div className="absolute inset-0 z-10 rounded-[8px] bg-white/70 backdrop-blur-[1px] flex flex-col items-center justify-center">
                <Spinner className="h-10 w-10 text-camber-blue-500" />
                <div className="mt-3 text-[14px] text-[#242424] font-semibold">
                  {typeof uploadProgress === "number" && uploadProgress >= 99
                    ? "Upload complete — waiting for server…"
                    : "Uploading…"}
                </div>
                <div className="mt-1 text-[12px] text-[#6B7280]">
                  {typeof uploadProgress === "number"
                    ? `${uploadProgress}%`
                    : "Please don’t close this window"}
                </div>
              </div>
            )}
            <form onSubmit={handleUpload} className="h-full px-[24px] pt-[24px] pb-[32px] flex flex-col gap-[24px]">
              {/* Header */}
              <div className="flex items-center gap-[16px]">
                <button
                  type="button"
                  onClick={() => {
                    if (uploadMutation.isPending) return;
                    closeUploadModal();
                  }}
                  disabled={uploadMutation.isPending}
                  className="w-[40px] h-[40px] rounded-[8px] bg-[#C9D8FC] flex items-center justify-center"
                  aria-label="Back"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5 6.5L9.5 12L14.5 17.5" stroke="#3C11BA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <div className="text-[18px] font-semibold leading-[155%] text-[#242424]">Upload Video &amp; Route</div>
              </div>

              {/* Inputs */}
              <div className="flex flex-col gap-[32px]">
                {/* Video */}
                <div className="flex flex-col gap-[8px]">
                  <div className="text-[14px] leading-[155%] text-[#212529]">Video</div>

                  <div className="flex items-center gap-[8px]">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleFileChange}
                      disabled={uploadMutation.isPending}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => videoInputRef.current?.click()}
                      disabled={uploadMutation.isPending}
                      className="box-border shrink-0 w-[110px] h-[32px] bg-[#FBFBFB] border border-[#CDCDCD] rounded-[4px] px-[24px] py-[8px] flex items-center justify-center whitespace-nowrap text-[14px] leading-[155%] text-[#0B0B0B] disabled:opacity-50"
                    >
                      Select file
                    </button>
                    <div className="flex-1 min-w-0 h-[40px] bg-white border border-[#E0E2E5] rounded-[8px] px-[8px] flex items-center">
                      <div className="text-[14px] leading-[155%] text-[#ADB5BD] truncate">
                        {file?.name || "File not selected"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Route */}
                <div className="flex flex-col gap-[8px]">
                  <div className="text-[14px] leading-[155%] text-[#212529]">Route</div>

                  <div className="flex items-center gap-[8px]">
                    <input
                      ref={routeInputRef}
                      type="file"
                      accept=".gpx"
                      onChange={handleRouteFileChange}
                      disabled={uploadMutation.isPending}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => routeInputRef.current?.click()}
                      disabled={uploadMutation.isPending}
                      className="box-border shrink-0 w-[110px] h-[32px] bg-[#FBFBFB] border border-[#CDCDCD] rounded-[4px] px-[24px] py-[8px] flex items-center justify-center whitespace-nowrap text-[14px] leading-[155%] text-[#0B0B0B] disabled:opacity-50"
                    >
                      Select file
                    </button>
                    <div className="flex-1 min-w-0 h-[40px] bg-white border border-[#E0E2E5] rounded-[8px] px-[8px] flex items-center">
                      <div className="text-[14px] leading-[155%] text-[#ADB5BD] truncate">
                        {routeFile?.name || "File not selected"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {validationError && (
                <div className="-mt-[12px] text-[12px] leading-[155%] text-red-700">
                  {validationError}
                </div>
              )}

              {/* Actions */}
              <div className="mt-auto flex items-center justify-between gap-[16px]">
                <button
                  type="button"
                  onClick={() => {
                    if (uploadMutation.isPending) return;
                    closeUploadModal();
                  }}
                  disabled={uploadMutation.isPending}
                  className="w-[179px] h-[40px] bg-[#E2DCF8] rounded-[4px] text-[16px] leading-[155%] text-[#2C1B6B]"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={uploadMutation.isPending || !file || !routeFile}
                  className="w-[180px] h-[40px] bg-[#5937D6] rounded-[4px] text-[16px] leading-[155%] text-[#E3DDF8] disabled:opacity-50"
                >
                  {uploadMutation.isPending ? "Uploading…" : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* List Card */}
      <div className={`w-full bg-[#FDFEFF] rounded-[8px] p-[8px] ${videos.length === 0 ? "min-h-[529px]" : ""}`}>
        {/* Filters bar */}
        <div
          ref={filtersBarRef}
          className="w-full min-h-[64px] bg-[#F4F7FA] rounded-[4px] px-[16px] py-[16px] flex flex-wrap items-center justify-between gap-y-[12px] relative"
        >
          <div className="flex flex-wrap items-center gap-[8px]">
            {/* Status */}
            <div ref={statusDropdownRef} className="relative w-[132px] h-[32px]">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setProcessedOpen(false);
                  setProblemsOpen(false);
                  setStatusOpen((v) => !v);
                }}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between"
                aria-haspopup="listbox"
                aria-expanded={statusOpen}
              >
                <span
                  className={`text-[12px] leading-[155%] ${statusFilter ? "text-[#333333]" : "text-[#939393]"}`}
                >
                  {statusFilter
                    ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)
                    : "Status"}
                </span>

                <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M7 10L12 15L17 10" fill="currentColor" />
                  </svg>
                </span>
              </button>

              {statusOpen && (
                <div className="absolute left-[1px] top-[32px] w-[128px] bg-white border border-[#E0E2E5] rounded-[4px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] z-20">
                  <div className="w-full py-[8px]">
                    <div className="w-full flex flex-col items-center gap-[8px]">
                      <button
                        type="button"
                        onClick={() => { setStatusFilter(""); setStatusOpen(false); }}
                        className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]"
                        role="option"
                        aria-selected={statusFilter === ""}
                      >
                        All
                      </button>
                      <div className="w-full border-t border-[#E0E2E5]" />
                      <button
                        type="button"
                        onClick={() => { setStatusFilter("green"); setStatusOpen(false); }}
                        className="w-full flex items-center justify-center"
                        role="option"
                        aria-selected={statusFilter === "green"}
                      >
                        <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#EBFBEE] bg-[#EBFBEE] text-[12px] leading-[155%] text-[#2F9E44]">
                          Green
                        </span>
                      </button>
                      <div className="w-full border-t border-[#E0E2E5]" />
                      <button
                        type="button"
                        onClick={() => { setStatusFilter("yellow"); setStatusOpen(false); }}
                        className="w-full flex items-center justify-center"
                        role="option"
                        aria-selected={statusFilter === "yellow"}
                      >
                        <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#FFF3BF] bg-[#FFF3BF] text-[12px] leading-[155%] text-[#9A6700]">
                          Yellow
                        </span>
                      </button>
                      <div className="w-full border-t border-[#E0E2E5]" />
                      <button
                        type="button"
                        onClick={() => { setStatusFilter("orange"); setStatusOpen(false); }}
                        className="w-full flex items-center justify-center"
                        role="option"
                        aria-selected={statusFilter === "orange"}
                      >
                        <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#FFE8CC] bg-[#FFE8CC] text-[12px] leading-[155%] text-[#D9480F]">
                          Orange
                        </span>
                      </button>
                      <div className="w-full border-t border-[#E0E2E5]" />
                      <button
                        type="button"
                        onClick={() => { setStatusFilter("red"); setStatusOpen(false); }}
                        className="w-full flex items-center justify-center"
                        role="option"
                        aria-selected={statusFilter === "red"}
                      >
                        <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#FFE3E3] bg-[#FFE3E3] text-[12px] leading-[155%] text-[#C92A2A]">
                          Red
                        </span>
                      </button>
                      <div className="w-full border-t border-[#E0E2E5]" />
                      <button
                        type="button"
                        onClick={() => { setStatusFilter("black"); setStatusOpen(false); }}
                        className="w-full flex items-center justify-center"
                        role="option"
                        aria-selected={statusFilter === "black"}
                      >
                        <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#343A40] bg-[#343A40] text-[12px] leading-[155%] text-[#FFFFFF]">
                          Black
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Processed */}
            <div ref={processedDropdownRef} className="relative w-[130px] h-[32px]">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatusOpen(false);
                  setProblemsOpen(false);
                  setProcessedOpen((v) => !v);
                }}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between"
                aria-haspopup="listbox"
                aria-expanded={processedOpen}
              >
                <span
                  className={`text-[12px] leading-[155%] min-w-0 flex-1 truncate whitespace-nowrap ${processedFilter ? "text-[#333333]" : "text-[#939393]"}`}
                >
                  {processedFilter === "" ? "Processed" : processedFilter === "true" ? "Processed" : "Unprocessed"}
                </span>

                <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M7 10L12 15L17 10" fill="currentColor" />
                  </svg>
                </span>
              </button>

              {processedOpen && (
                <div className="absolute left-0 top-[32px] w-[128px] h-[105px] bg-white border-t border-[#E0E2E5] rounded-[4px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] z-20">
                  <div className="h-full flex flex-col items-center justify-center gap-[8px]">
                    <button
                      type="button"
                      onClick={() => {
                        setProcessedFilter("");
                        setProcessedOpen(false);
                      }}
                      className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]"
                      role="option"
                      aria-selected={processedFilter === ""}
                    >
                      All
                    </button>
                    <div className="w-full border-t border-[#E0E2E5]" />
                    <button
                      type="button"
                      onClick={() => {
                        setProcessedFilter("true");
                        setProcessedOpen(false);
                      }}
                      className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]"
                      role="option"
                      aria-selected={processedFilter === "true"}
                    >
                      Processed
                    </button>
                    <div className="w-full border-t border-[#E0E2E5]" />
                    <button
                      type="button"
                      onClick={() => {
                        setProcessedFilter("false");
                        setProcessedOpen(false);
                      }}
                      className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]"
                      role="option"
                      aria-selected={processedFilter === "false"}
                    >
                      Unprocessed
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Problems Found */}
            <div ref={problemsDropdownRef} className="relative w-[164px] h-[32px]">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setStatusOpen(false);
                  setProcessedOpen(false);
                  if (problemsOpen) {
                    setProblemsOpen(false);
                    return;
                  }
                  syncProblemsRangeFromFilters();
                  setProblemsOpen(true);
                }}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center gap-[32px]"
                aria-haspopup="dialog"
                aria-expanded={problemsOpen}
              >
                <span className="text-[12px] leading-[155%] text-[#939393] whitespace-nowrap">Problems Found</span>
                <span className="ml-auto w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M7 10L12 15L17 10" fill="currentColor" />
                  </svg>
                </span>
              </button>

              {problemsOpen && (
                <div className="absolute left-0 top-[32px] w-[211px] h-[58px] bg-white border-t border-[#E0E2E5] rounded-[4px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] z-20">
                  <div className="w-full h-full px-[16px] pt-[16px] pb-[8px] flex flex-col items-center justify-center gap-[8px]">
                    <div className="relative w-[179px] h-[34px]">
                      <div className="absolute left-0 top-0 w-[179px] h-[34px] flex flex-col items-end gap-[5px]">
                        <div className="relative w-[179px] h-[11px]">
                          <Range
                            values={problemsRange}
                            step={1}
                            min={PROBLEMS_MIN}
                            max={PROBLEMS_MAX}
                            onChange={(values) => {
                              setProblemsRange(values);
                              setMinMalfunctions(String(values[0]));
                              setMaxMalfunctions(String(values[1]));
                            }}
                            renderTrack={({ props, children }) => {
                              const leftPct = ((problemsRange[0] - PROBLEMS_MIN) / (PROBLEMS_MAX - PROBLEMS_MIN)) * 100;
                              const rightPct = ((problemsRange[1] - PROBLEMS_MIN) / (PROBLEMS_MAX - PROBLEMS_MIN)) * 100;

                              return (
                                <div
                                  onMouseDown={props.onMouseDown}
                                  onTouchStart={props.onTouchStart}
                                  className="absolute left-0 top-0 w-[179px] h-[11px] flex"
                                >
                                  <div
                                    ref={props.ref}
                                    className="relative w-full h-[6px] bg-[#D9D9D9] rounded-[55px] self-center"
                                  >
                                    <div
                                      className="absolute inset-y-0 rounded-[55px]"
                                      style={{
                                        left: `${leftPct}%`,
                                        width: `${Math.max(0, rightPct - leftPct)}%`,
                                        backgroundImage: "linear-gradient(180deg, #DA3272 0%, #5438CE 100%)",
                                      }}
                                    />
                                    {children}
                                  </div>
                                </div>
                              );
                            }}
                            renderThumb={({ props }) => (
                              <div
                                {...props}
                                className="w-[11px] h-[11px] rounded-[55px] outline-none"
                                style={{
                                  ...props.style,
                                  backgroundImage: "linear-gradient(180deg, #DA3272 0%, #5438CE 100%)",
                                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.25)",
                                }}
                              />
                            )}
                          />
                        </div>

                        <div className="w-[179px] h-[18px] flex items-center justify-between">
                          <div className="w-[32px] h-[18px] px-[4px] py-[2px] flex items-center justify-center rounded-[2px]">
                            <div className="text-[10px] leading-[155%] text-[#AAACAF]">{problemsRange[0]}</div>
                          </div>
                          <div className="w-[32px] h-[18px] px-[4px] py-[2px] flex items-center justify-center rounded-[2px]">
                            <div className="text-[10px] leading-[155%] text-[#AAACAF]">{problemsRange[1]}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>


            {/* Start Date */}
            <div ref={startCalendarTriggerRef} className="relative w-[129px] h-[32px]">
              <button
                type="button"
                onClick={() => openCalendar("start")}
                aria-haspopup="true"
                aria-expanded={calendarOpenFor === "start"}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
              >
                <span className={startDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                  {startDate ? formatCalendarInput(startDate) : "Start Date"}
                </span>
                <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M7 10L12 15L17 10" fill="currentColor" />
                  </svg>
                </span>
              </button>
            </div>

            {/* End Date */}
            <div ref={endCalendarTriggerRef} className="relative w-[123px] h-[32px]">
              <button
                type="button"
                onClick={() => openCalendar("end")}
                aria-haspopup="true"
                aria-expanded={calendarOpenFor === "end"}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
              >
                <span className={endDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                  {endDate ? formatCalendarInput(endDate) : "End Date"}
                </span>
                <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M7 10L12 15L17 10" fill="currentColor" />
                  </svg>
                </span>
              </button>
            </div>

            {calendarOpenFor && (
              <div
                ref={calendarDropdownRef}
                className="absolute z-30 w-[222px] h-[249px] bg-white border border-[#E0E2E5] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] rounded-[4px]"
                style={{ top: calendarPosition.top, left: calendarPosition.left }}
              >
                {/* Header: padding 10px 14px, gap 8px */}
                <div className="flex flex-col items-start px-[14px] py-[10px] gap-[8px]">
                  <div className="w-full h-[22px] flex items-center justify-between">
                    {/* Month label: 16/22 regular */}
                    <div className="text-[16px] leading-[22px] font-normal text-[#333333]">
                      {activeMonthLabel}
                    </div>

                    {/* Year controls: 11px arrows, 12/16 year */}
                    <div className="h-[16px] flex items-center justify-center gap-[4px]">
                      <button
                        type="button"
                        onClick={() => handleCalendarNav(-1)}
                        className="w-[11px] h-[11px] flex items-center justify-center"
                        aria-label="Previous month"
                      >
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.5 2.5L4.5 6L7.5 9.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>

                      <div className="w-[28px] text-center text-[12px] leading-[16px] font-normal text-[#333333]">
                        {calendarYear}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleCalendarNav(1)}
                        className="w-[11px] h-[11px] flex items-center justify-center"
                        aria-label="Next month"
                      >
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.5 9.5L7.5 6L4.5 2.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Calendar grid area */}
                <div className="w-full flex flex-col items-start px-[7px]">
                  {/* Weekday labels: 10/14, opacity 0.7, lowercase */}
                  <div className="w-[208px] h-[32px] flex items-start justify-between opacity-70">
                    {calendarWeekdayLabels.map((label, idx) => (
                      <div key={`${label}-${idx}`} className="w-[30px] h-[32px] p-[4px] flex items-start justify-center">
                        <div className="w-[22px] h-[24px] flex items-center justify-center">
                          <span className="text-[10px] leading-[14px] font-normal text-[#333333]">
                            {label.toLowerCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Weeks */}
                  <div className="w-[208px] flex flex-col items-start">
                    {calendarMatrix.map((week, weekIndex) => (
                      <div key={weekIndex} className="w-[208px] h-[32px] flex items-start justify-between">
                        {week.map((day, dayIndex) => {
                          const isSelectedStart = isSameDay(day.date, parsedStartDate);
                          const isSelectedEnd = isSameDay(day.date, parsedEndDate);

                          // Keep your range logic, but constrain highlight to the inner pill
                          const inRange = isWithinRange(day.date, parsedStartDate, parsedEndDate);

                          const isSelected = isSelectedStart || isSelectedEnd;

                          // Figma: out-of-month opacity 0.3
                          const cellOpacity = day.inMonth ? "" : "opacity-30";

                          const pillClass = isSelected
                            ? "bg-gradient-to-b from-[#DA3272] to-[#5438CE] text-white"
                            : inRange
                              ? "bg-[#F3E8FF] text-[#333333]"
                              : "bg-transparent text-[#333333]";

                          return (
                            <button
                              key={`${weekIndex}-${dayIndex}`}
                              type="button"
                              onClick={() => handleDaySelect(day.date)}
                              className={`w-[30px] h-[32px] p-[4px] flex items-start justify-center ${cellOpacity}`}
                            >
                              {/* Inner pill: 22x22 for selected, 22x24 otherwise (matches figma proportions) */}
                              <span
                                className={`${
                                  isSelected ? "w-[22px] h-[22px]" : "w-[22px] h-[24px]"
                                } rounded-[99px] flex items-center justify-center ${pillClass}`}
                              >
                                <span className={`text-[10px] leading-[14px] font-normal ${isSelected ? "text-white" : ""}`}>
                                  {day.label.padStart(2, "0")}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer: Clear (left) */}
                <div className="px-[14px] py-[10px] flex items-center justify-start">
                  <button type="button" onClick={clearCalendarSelection} className="text-[12px] leading-[16px] text-[#5937D6]">
                    Clear
                  </button>
                </div>
              </div>
            )}


            {/* Only unsolved */}
            <label className="flex items-center gap-[8px] pl-[8px] select-none cursor-pointer">
              <input
                type="checkbox"
                checked={unsolvedOnly}
                onChange={(e) => setUnsolvedOnly(e.target.checked)}
                className="w-[16px] h-[16px] rounded-[2px] border border-[#1C1B1F]"
              />
              <span className="text-[12px] leading-[155%] text-[#000000]">Only Unsolved Detections</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-[12px] ml-auto">
            {(statusFilter || processedFilter || minMalfunctions || maxMalfunctions || unsolvedOnly || startDate || endDate) && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-[12px] leading-[155%] text-[#5937D6] hover:underline"
              >
                Clear
              </button>
            )}
          {totalCount > 0 && (
            <Pagination
              variant="compact"
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={(next) => setPage(next)}
              onPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              }}
            />
          )}
          </div>
        </div>

        {/* Table */}
        <div className="mt-[12px] w-full overflow-x-auto">
          <div className="min-w-[1294px] rounded-[4px] overflow-hidden">
            {/* Header */}
            <div className="h-[34px] border-b border-[#E0E2E5] bg-[#FDFEFF]">
              <div className="h-full pl-[24px] pr-[12px] grid grid-cols-[145px_154px_152px_92px_107px_80px_1fr] items-center text-[12px] leading-[155%] text-[#828282]">
                <div>Name</div>
                <div><SortHeader field="timestamp">Date</SortHeader></div>
                <div>Time</div>
                <div>Surface</div>
                <div><SortHeader field="processed">Processed</SortHeader></div>
                <div><SortHeader field="malfunction_count">Problems</SortHeader></div>
                <div className="justify-self-end" />
              </div>
            </div>

            {/* Rows */}
            {videos.length === 0 ? (
              <div className="h-[420px] flex flex-col items-center justify-center">
                <EmptyVideosIcon />
                <div className="mt-[18px] text-[12px] leading-[155%] text-[#969696]">
                  {emptyMessage}
                </div>
              </div>
            ) : (
              videos.map((v) => {
                const problems = v.malfunction_count ?? v.detections?.length ?? 0;

                return (
                  <div
                    key={v.id}
                    className="group h-[52px] border-b transition-colors bg-[#FDFEFF] border-[#E0E2E5] hover:bg-[#F3F5FE] hover:border-[#C9D8FC]"
                  >
                    <div className="h-full pl-[24px] pr-[12px] grid grid-cols-[145px_154px_152px_92px_107px_80px_1fr] items-center">
                      <div className="text-[14px] leading-[155%] text-[#000000]">Video #{v.id}</div>
                      <div className="text-[14px] leading-[155%] text-[#000000]">{formatDate(v.timestamp)}</div>
                      <div className="text-[14px] leading-[155%] text-[#000000]">{formatTime(v.timestamp)}</div>
                      <div>
                        {v.processed ? (
                          <SurfaceRatingBadge
                            status={v.status}
                            weightedScore={v.weighted_malfunction_score}
                            weightedPointsPerKm={v.weighted_malfunction_points_per_km}
                            details={v.surface_rating_details}
                          />
                        ) : (
                          <span className="text-[14px] leading-[155%] text-[#828282]">–</span>
                        )}
                      </div>
                      <div>
                        {v.processed ? (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#3ECC79" />
                          </svg>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16.59 7.41L12 12L7.41 7.41L6 8.82L10.59 13.41L6 18L7.41 19.41L12 14.82L16.59 19.41L18 18L13.41 13.41L18 8.82L16.59 7.41Z" fill="#CC3E3E" />
                          </svg>
                        )}
                      </div>
                      <div className="text-[14px] leading-[155%] text-[#000000]">{problems}</div>

                      {/* Actions */}
                      <div className="justify-self-end flex items-center gap-[12px]" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveAction(`delete:${v.id}`);
                            handleDelete(e, v.id);
                          }}
                          disabled={deleteMutation.isPending}
                          className={`w-[39px] h-[40px] rounded-[8px] flex items-center justify-center transition-colors disabled:opacity-50
                            ${activeAction === `delete:${v.id}`
                              ? "bg-[#D4DAFE] text-[#483ED1]"
                              : "bg-transparent text-[#BBBABB] hover:bg-[#D4DAFE] hover:text-[#483ED1]"
                            }`}
                          title="Delete"
                        >
                          <DeleteTileIcon />
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveAction(`view:${v.id}`);
                            openPreview(v);
                            setIsPreviewOpen(true);
                          }}
                          className={`w-[39px] h-[40px] rounded-[8px] flex items-center justify-center transition-colors
                            ${activeAction === `view:${v.id}`
                              ? "bg-[#D4DAFE] text-[#483ED1]"
                              : "bg-transparent text-[#BBBABB] hover:bg-[#D4DAFE] hover:text-[#483ED1]"
                            }`}
                          title="View"
                        >
                          <ViewTileIcon />
                        </button>

                        {/* Export (split) */}
                        <div className="relative" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const exportActive = exportMenuFor === v.id || exporting[`${v.id}-csv`] || exporting[`${v.id}-geojson`];
                            return (
                              <div
                                className={
                                  exportMenuFor === v.id
                                    ? "w-[66px] h-[40px] rounded-t-[8px] bg-white overflow-hidden text-[#473D93]"
                                    : `w-[66px] h-[40px] rounded-[8px] overflow-hidden transition-colors
                                      ${exportActive
                                        ? "bg-[#D4DAFE] text-[#483ED1]"
                                        : "bg-transparent text-[#BBBABB] hover:bg-[#D4DAFE] hover:text-[#483ED1]"
                                      }`
                                }
                                title="Download"
                              >
                            <div className="absolute inset-0 pointer-events-none">
                              <DownloadSplitTileIcon />
                            </div>

                            <button
                              type="button"
                              // onClick={(e) => handleExport(e, v.id, "csv")}
                              disabled={exporting[`${v.id}-csv`]}
                              className="absolute left-0 top-0 h-full w-[36px] flex items-center justify-center disabled:opacity-50"
                              title="Download (CSV)"
                            >
                              {exporting[`${v.id}-csv`] && (
                                <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                                  <Spinner className="h-5 w-5" />
                                </span>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (exportMenuFor === v.id) {
                                  setExportMenuFor(null);
                                } else {
                                  openExportMenuAtEvent(v.id, e);
                                }
                              }}
                              className="absolute right-0 top-0 h-full w-[30px] flex items-center justify-center"
                              title="More"
                              aria-haspopup="menu"
                              aria-expanded={exportMenuFor === v.id}
                            />

                                {/* Divider overlay that matches currentColor */}
                                <div
                                  className={
                                    exportMenuFor === v.id
                                      ? "absolute left-[36px] top-[7px] bottom-[7px] w-[1px] bg-current opacity-100"
                                      : "absolute left-[36px] top-[7px] bottom-[7px] w-[1px] bg-current opacity-100"
                                  }
                                />
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        
      </div>

      {/* Footer (outside the white card) */}
      <div className="w-full mt-[12px] flex items-center justify-between">
        <div className="text-[12px] leading-[155%] text-[#969696]">
          {totalCount === 0 ? "Showing 0 results" : `Showing ${showingFrom}-${showingTo} results`}
        </div>
      </div>

      {exportMenuFor &&
        createPortal(
          <div
            className="fixed z-[80]"
            style={{ top: exportMenuPosition.top, left: exportMenuPosition.left }}
            onClick={(e) => e.stopPropagation()}
            role="menu"
            aria-label="Export menu"
          >
            <div className="w-[66px] bg-white border border-[#E3EBFF] rounded-[8px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] overflow-hidden">
              <button
                type="button"
                onClick={(e) => {
                  handleExport(e, exportMenuFor, "geojson");
                  setExportMenuFor(null);
                }}
                className="w-full h-[38px] flex items-center justify-center text-[14px] leading-[155%] text-[#473D93] hover:bg-[#F4F7FA]"
              >
                .JSON
              </button>
              <div className="w-full h-[1px] bg-[#E3EBFF]" />
              <button
                type="button"
                onClick={(e) => {
                  handleExport(e, exportMenuFor, "csv");
                  setExportMenuFor(null);
                }}
                className="w-full h-[38px] flex items-center justify-center text-[14px] leading-[155%] text-[#473D93] hover:bg-[#F4F7FA]"
              >
                .CSV
              </button>
            </div>
          </div>,
          document.body
        )}

      {/* Preview + Detections */}
      {selected && isPreviewOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[90]"
            onClick={() => {
              setIsPreviewOpen(false);
              setSelected(null);
              resetAnnotatedState();
              resetMapPreviewState();
              setActiveAction(null);
            }}
          >
            {/* Rectangle 549 backdrop */}
            <div className="absolute inset-0 bg-[#131313]/[0.69]" />

            {/* Center wrapper */}
            <div className="absolute inset-0 flex items-center justify-center p-[16px]">
              {/* Rectangle 550 */}
              <div
                className="relative bg-white rounded-[8px] overflow-hidden"
                style={{
                  width: 1335,
                  height: 625,
                  maxWidth: "calc(100vw - 32px)",
                  maxHeight: "calc(100vh - 32px)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header row */}
                <div className="absolute left-[24px] top-[24px] right-[24px] h-[44px] flex items-center justify-between">
                  <div className="flex items-center gap-[16px]">
                    {/* Back button */}
                    <button
                      type="button"
                      onClick={() => {
                        setIsPreviewOpen(false);
                        setSelected(null);
                        resetAnnotatedState();
                        resetMapPreviewState();
                        setActiveAction(null);
                      }}
                      className="w-[40px] h-[40px] rounded-[8px] bg-[#C9D8FC] flex items-center justify-center"
                      aria-label="Close preview"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M14.5 6.5L9.5 12L14.5 17.5"
                          stroke="#3C11BA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {/* Title */}
                    <div className="font-['Open_Sans'] font-semibold text-[24px] leading-[155%] text-[#242424]">
                      Video #{selected.id}
                    </div>
                  </div>

                  {/* View Annotated button (top-right) */}
                  {/* {selected.processed && selected.malfunction_count > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAnnotated((prev) => !prev)}
                      className="h-[40px] px-[18px] rounded-[8px] bg-[#7B47F3] text-white font-['Open_Sans'] font-semibold text-[14px] leading-[155%] disabled:opacity-50"
                      disabled={exporting[`${selected.id}-annotated`]}
                    >
                      {exporting[`${selected.id}-annotated`] ? "Loading…" : showAnnotated ? "View Original" : "View Annotated"}
                    </button>
                  )} */}
                </div>

                {/* Video / map block */}
                <div
                  className={`absolute left-[24px] top-[92px] rounded-[8px] ${
                    previewMode === "map" ? "bg-transparent" : "overflow-hidden bg-[#1f1f1f]"
                  }`}
                  style={{
                    width: 980, // fits with right panel at 316 and spacing
                    height: 525,
                    maxWidth: "calc(100% - 24px - 24px - 316px - 16px)",
                    maxHeight: "calc(100% - 92px - 24px)",
                  }}
                >
                  {previewMode === "map" ? (
                    <div className="grid w-full h-full gap-[16px] grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)]">
                      <div className="rounded-[8px] overflow-hidden bg-[#1f1f1f]">
                        <video
                          ref={videoRef}
                          controls
                          className="w-full h-full object-contain"
                          src={showAnnotated ? annotatedUrl : (previewUrl || makeFullUrl(selected.video))}
                          key={showAnnotated ? "annotated-map" : "original-map"}
                        />
                      </div>

                      <div className="relative rounded-[8px] overflow-hidden bg-[#F4F7FB] border border-[#E0E2E5]">
                        <div className="absolute left-[12px] top-[12px] z-[500] flex items-center gap-[8px]">
                          <button
                            type="button"
                            onClick={() => setPreviewMode("video")}
                            className="h-[32px] px-[12px] rounded-[4px] bg-white border border-[#E0E2E5] shadow-sm font-['Open_Sans'] text-[13px] leading-[155%] text-[#242424]"
                          >
                            Hide map
                          </button>
                          {selectedDetectionForMap && (
                            <div className="h-[32px] px-[10px] rounded-[4px] bg-white border border-[#E0E2E5] shadow-sm flex items-center font-['Open_Sans'] text-[13px] leading-[155%] text-[#242424]">
                              {selectedDetectionForMap.timecode?.toFixed?.(2)} s (Frame {selectedDetectionForMap.frame})
                            </div>
                          )}
                        </div>

                        {routeLoading ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#F4F7FB]">
                            <Spinner className="h-10 w-10 text-camber-blue-500 mb-[12px]" />
                            <div className="font-['Open_Sans'] text-[14px] text-[#6B7280]">Loading route map...</div>
                          </div>
                        ) : routeError ? (
                          <div className="w-full h-full flex items-center justify-center bg-[#F4F7FB] font-['Open_Sans'] text-[14px] text-[#6B7280]">
                            {routeError}
                          </div>
                        ) : (
                          <DetectionRouteMap
                            routeGeom={selectedRoute?.geom}
                            detection={selectedDetectionForMap}
                            detections={selected.detections ?? []}
                            height="100%"
                            onMarkerClick={(detection) => {
                              setSelectedDetectionForMap(detection);
                              seekVideoToDetection(detection);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <video
                      ref={videoRef}
                      controls
                      className="w-full h-full object-contain"
                      src={showAnnotated ? annotatedUrl : (previewUrl || makeFullUrl(selected.video))}
                      key={showAnnotated ? "annotated" : "original"}
                    />
                  )}
                </div>

                {/* Right panel group (Rectangle 552) */}
                <div
                  className="absolute rounded-[8px]"
                  style={{
                    left: "calc(100% - 24px - 316px)",
                    top: 92,
                    width: 316,
                    height: 525,
                    background: "#FEFEFE",
                    border: "1px solid #E0E2E5",
                  }}
                >
                  {/* Title */}
                  <div
                    className="font-['Open_Sans'] font-semibold text-[16px] leading-[155%] text-[#242424]"
                    style={{ padding: "16px 16px 8px 16px" }}
                  >
                    Detected Malfunctions
                  </div>

                  {/* Scrollable body */}
                  <div
                    className="overflow-y-auto"
                    style={{
                      height: "calc(525px - 16px - 25px - 8px)",
                      paddingBottom: 8,
                    }}
                  >
                    {(selected.detections ?? []).length === 0 ? (
                      <div className="px-[16px] text-[14px] text-[#868E96] font-['Open_Sans']">
                        None found
                      </div>
                    ) : (
                      (selected.detections ?? []).map((d, idx) => (
                        <div key={d.id}>
                          <div
                            className="flex flex-row justify-between items-end"
                            style={{ padding: "0px 16px", height: 24, marginTop: idx === 0 ? 8 : 16 }}
                          >
                            <button
                              type="button"
                              className="font-['Open_Sans'] font-semibold text-[14px] leading-[155%] text-[#221C97]"
                              onClick={() => jumpToDetection(d)}
                            >
                              {d.timecode.toFixed(2)} s (Frame {d.frame})
                            </button>

                            <div className="flex items-center gap-[8px]">
                              {/* Eye icon */}
                              <button
                                type="button"
                                className="w-[24px] h-[24px] flex items-center justify-center"
                                onClick={() => jumpToDetection(d)}
                                title="Jump to detection"
                              >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M12 5C7 5 3.27 8.11 2 12c1.27 3.89 5 7 10 7s8.73-3.11 10-7c-1.27-3.89-5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
                                    fill="#BFBFBF"
                                  />
                                  <circle cx="12" cy="12" r="2" fill="#BFBFBF" />
                                </svg>
                              </button>

                              <button
                                type="button"
                                className={`w-[24px] h-[24px] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed ${
                                  previewMode === "map" && selectedDetectionForMap?.id === d.id ? "rounded-[4px] outline outline-2 outline-[#5937D6]" : ""
                                }`}
                                onClick={() => openDetectionMap(d)}
                                disabled={!hasDetectionCoords(d)}
                                title={hasDetectionCoords(d) ? "Show detection on map" : "No GPS coordinate available"}
                              >
                                <DetectionMapIcon
                                  fill={
                                    previewMode === "map" && selectedDetectionForMap?.id === d.id
                                      ? "#5937D6"
                                      : hasDetectionCoords(d) ? "#BFBFBF" : "#D7D7D7"
                                  }
                                />
                              </button>
                            </div>
                          </div>

                          {/* Checkboxes group */}
                          <div style={{ padding: "8px 16px 0px 16px" }}>
                            <div className="mb-[8px] flex items-center gap-[8px] font-['Open_Sans'] text-[12px] leading-[155%]">
                              <span className="text-[#6B7280]">Type</span>
                              <span className="inline-flex rounded-full bg-[#EEEAFE] px-[8px] py-[2px] font-semibold text-[#483ED1]">
                                {getMalfunctionTypeLabel(d)}
                              </span>
                            </div>

                            <label className="flex items-center gap-[8px] h-[22px] font-['Open_Sans'] text-[14px] leading-[155%] text-[#000000]">
                              <input
                                type="checkbox"
                                className="w-[16px] h-[16px]"
                                checked={d.user_confirmed === true}
                                onChange={() => handleCheckboxChange(d.id, "user_confirmed", d.user_confirmed)}
                                disabled={updateDetectionMut.isPending}
                              />
                              User Confirmed
                            </label>

                            <div className="h-[8px]" />

                            <label className="flex items-center gap-[8px] h-[22px] font-['Open_Sans'] text-[14px] leading-[155%] text-[#000000]">
                              <input
                                type="checkbox"
                                className="w-[16px] h-[16px]"
                                checked={d.field_confirmed === true}
                                onChange={() => handleCheckboxChange(d.id, "field_confirmed", d.field_confirmed)}
                                disabled={updateDetectionMut.isPending}
                              />
                              Verified On-Site
                            </label>

                            <div className="h-[8px]" />

                            <label className="flex items-center gap-[8px] h-[22px] font-['Open_Sans'] text-[14px] leading-[155%] text-[#000000]">
                              <input
                                type="checkbox"
                                className="w-[16px] h-[16px]"
                                checked={d.field_solved === true}
                                onChange={() => handleCheckboxChange(d.id, "field_solved", d.field_solved)}
                                disabled={updateDetectionMut.isPending}
                              />
                              Solved by Stakeholder
                            </label>
                          </div>

                          {/* Divider line (Line 27) */}
                          <div
                            style={{
                              height: 1,
                              background: "#E5E7EA",
                              width: 300,
                              margin: "16px auto 0px auto",
                            }}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => {
          setDeleteConfirm({ isOpen: false, videoId: null }); 
          setActiveAction(null); 
          setIsPreviewOpen(false);
          resetMapPreviewState();
        }}
        onConfirm={confirmDelete}
        title="Delete Video"
        message="Delete this video? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
